import { FC, useEffect, useState, useRef, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import makeStyles from '@mui/styles/makeStyles';
import { Divider, Grid, Theme, Typography, useMediaQuery, Box } from '@mui/material';
import { Loader, Page, PageTitleWithBack, Button, Table, Link } from '../../../components';
import { formatDate, formatMoney } from '../../../helpers';
import ReactToPrint from 'react-to-print';
import { Print } from '@mui/icons-material';
import { getPaymentDetails } from '../../../fetch/payments';
import { ICheckInvoice, ICheckPayment } from '../../../models/payments';
import { CheckDetailsMobile } from './CheckDetailsMobile';
import { useParams } from 'react-router-dom';

interface ICheckDetails {}

interface ICheckInvoiceColumns extends ICheckInvoice {
  date: string;
}

export const CheckDetails: FC<ICheckDetails> = () => {
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery('(max-width: 1058px)');
  const [isLoadingCheckDetails, setIsLoadingCheckDetails] = useState<boolean>(true);
  const [isPrint, setIsPrint] = useState(false);
  const [checkDetails, setCheckDetails] = useState<ICheckPayment>(null);
  const printRef = useRef();
  const { checkNumber }: { checkNumber: string } = useParams();
  const params = new URLSearchParams(window.location.search);
  const classes = useStyles();

  const fetchCheckDetails = async () => {
    try {
      setIsLoadingCheckDetails(true);

      const res = await getPaymentDetails(params.get('type'), checkNumber, params.get('vendorId'));

      setCheckDetails(res);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Something went wrong. ${error}`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingCheckDetails(false);
    }
  };

  useEffect(() => {
    fetchCheckDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lineItemColumns = useMemo(() => {
    return [
      {
        Header: 'Invoice No.',
        accessor: 'invoiceNumber',
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ICheckInvoiceColumns } };
        }) => {
          return <Link to={`/vendors/invoices/${original.recNum}`}>{original.invoiceNumber}</Link>;
        }
      },
      {
        Header: 'Submit Date',
        accessor: 'dateSubmitted',
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ICheckInvoiceColumns } };
        }) => {
          return <span>{formatDate(original.dateSubmitted)}</span>;
        }
      },
      {
        Header: 'Paid Date',
        accessor: '',
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ICheckInvoiceColumns } };
        }) => {
          return <span>{formatDate(original.date)}</span>;
        }
      },
      {
        Header: 'Description',
        accessor: 'description'
      },
      {
        Header: 'Invoice Amount',
        accessor: 'invoiceAmount',
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ICheckInvoiceColumns } };
        }) => {
          return <span>{formatMoney(original.invoiceAmount)}</span>;
        }
      },
      {
        Header: 'Paid Amount',
        accessor: 'paidAmount',
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ICheckInvoiceColumns } };
        }) => {
          return <span>{formatMoney(original.paidAmount)}</span>;
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkDetails]);

  return (
    <Page title={`Check Details ${checkNumber}`} showSideBar={false} sideBarLinks={[]}>
      <Grid container className={classes.pageContainer}>
        <Grid item xs={12} md={10} className={classes.pageTitleWrapper}>
          <PageTitleWithBack
            showDivider={false}
            backText={'Back to Payments'}
            backUrl={'/vendors/payments'}
            title={`Check Details ${checkNumber}`}
          />
        </Grid>
        <Grid item xs={12} md={2} className={classes.infoWrapper}>
          {checkDetails && (
            <ReactToPrint
              // This flag will change the mobile component to the desktop version so print works in mobile
              onBeforeGetContent={async () => {
                await setIsPrint(true);
              }}
              trigger={() => {
                return (
                  <Button
                    id="Print"
                    startIcon={<Print />}
                    type="button"
                    color="primary"
                    variant="contained"
                  >
                    Print
                  </Button>
                );
              }}
              onAfterPrint={() => {
                setIsPrint(false);
              }}
              content={() => printRef.current}
            />
          )}
        </Grid>
      </Grid>
      <Divider />
      {isLoadingCheckDetails && <Loader type="overlay" position="centered" />}
      {!isLoadingCheckDetails && !checkDetails && (
        <Box
          display="flex"
          height="10rem"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Typography>Check does not exist</Typography>
          <Link to="/vendors/payments">Back to payments</Link>
        </Box>
      )}
      {checkDetails && (
        <div className={classes.invoiceCard} ref={printRef}>
          <Typography variant="h5" gutterBottom className="print-header">
            Payment Invoice Details
          </Typography>
          <Box marginBottom="1rem">
            <Grid container spacing={1}>
              <Grid item xs={6} sm={4}>
                <Typography color="primary">Vendor</Typography>
                <Typography className={classes.paymentInvoiceDetails} variant="h6" component="div">
                  {checkDetails.vendor && checkDetails.vendor.name ? checkDetails.vendor.name : ''}
                </Typography>
                <Typography color="primary">Type</Typography>
                <Typography className={classes.paymentInvoiceDetails} variant="h6" component="div">
                  {checkDetails.vendor && checkDetails.vendor.type ? checkDetails.vendor.type : ''}
                </Typography>
                <Typography color="primary">Sub Type</Typography>
                <Typography className={classes.paymentInvoiceDetails} variant="h6" component="div">
                  {checkDetails.vendor && checkDetails.vendor.subType
                    ? checkDetails.vendor.subType
                    : ''}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Typography color="primary">Check Number</Typography>
                <Typography className={classes.paymentInvoiceDetails} variant="h6" component="div">
                  {checkDetails?.number ?? ''}
                </Typography>
                <Typography color="primary">Check Amount</Typography>
                <Typography className={classes.paymentInvoiceDetails} variant="h6" component="div">
                  {checkDetails.amount ? formatMoney(checkDetails.amount) : 0}
                </Typography>
                <Typography color="primary">Check Date</Typography>
                <Typography className={classes.paymentInvoiceDetails} variant="h6" component="div">
                  {checkDetails.date ? formatDate(checkDetails.date) : ''}
                </Typography>
              </Grid>
            </Grid>
          </Box>
          <Divider />
          <Box marginTop="8px">
            <Typography className={classes.paymentInvoiceDetails} variant="h6" component="div">
              Associated Invoices
            </Typography>
          </Box>
          <div className="page-break" />
          <Table
            columns={lineItemColumns}
            data={checkDetails.invoices.map(invoice => {
              return { ...invoice, date: checkDetails.date };
            })}
            isLoading={isLoadingCheckDetails}
            ResponsiveComponent={isMobile && !isPrint ? CheckDetailsMobile : undefined}
            hidePagination
          />
        </div>
      )}
    </Page>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  pageTitleWrapper: {},
  paymentInvoiceDetails: {
    fontSize: theme.typography.subtitle1.fontSize
  },
  wrapper: {
    marginTop: '2rem'
  },
  tabBar: {
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  },
  infoWrapper: {
    textAlign: 'center',
    marginBottom: theme.spacing(0.5),
    '@media (min-width: 900px)': {
      textAlign: 'right'
    }
  },
  dateWrapper: {
    textAlign: 'center',
    marginBottom: theme.spacing(0.5),
    '@media (min-width: 600px)': {
      textAlign: 'center'
    }
  },
  pageWrapper: {
    marginTop: 0
  },
  cardRoot: {},
  content: {
    padding: 24,
    textAlign: 'center'
  },
  pageContainer: {
    justifyContent: 'space-around',
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'normal'
    }
  },
  selectedTradePartner: {
    textAlign: 'left',
    minWidth: '150px',
    maxWidth: '100%',
    marginBottom: theme.spacing(0.5)
  },
  invoiceCard: {
    marginTop: '1rem'
  },
  lineItems: {
    marginTop: theme.spacing(1)
  },
  total: {
    padding: theme.spacing(1),
    textAlign: 'right'
  },
  lastViewed: {
    fontWeight: theme.typography.fontWeightLight,
    fontStyle: 'italic'
  }
}));
